import type { PickedFile } from '../pickers/types';

export type OutputFormat = 'jpeg' | 'png';

export interface FileDto {
  id: string;
  token: string;
  status: 'converted' | 'failed';
  originalName: string;
  outputName: string;
  format: OutputFormat;
  originalBytes: number;
  outputBytes: number;
  width: number;
  height: number;
  source: 'device' | 'dropbox' | 'google-drive' | 'onedrive';
  error?: string;
  createdAt: number;
  expiresAt: number;
  downloadUrl?: string;
}

export interface ServerConfig {
  maxFileBytes: number;
  /** Files per batch for the signed-in account. `null` means no limit. */
  maxFilesPerBatch: number | null;
  retentionHours: number;
  formats: OutputFormat[];
}

/** Lifetime totals for the account. These outlive the 24-hour file retention. */
export interface AccountStats {
  converted: number;
  failed: number;
  originalBytes: number;
  outputBytes: number;
  firstAt: number;
  updatedAt: number;
}

export interface ConvertResult {
  files: FileDto[];
  /** Absent when the server could not update the totals; refetch in that case. */
  stats?: AccountStats;
}

const BASE = (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/$/, '') ?? '';

/** Cookies carry the session, so every call must send credentials. */
async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(BASE + path, { credentials: 'include', ...init });

  if (!response.ok) {
    let message = 'Request failed (' + response.status + ')';
    try {
      const body = (await response.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      /* non-JSON error body */
    }
    throw new Error(message);
  }

  return (await response.json()) as T;
}

export function apiUrl(path: string): string {
  return BASE + path;
}

export function getConfig(): Promise<ServerConfig> {
  return request<ServerConfig>('/api/config');
}

export function getStats(): Promise<AccountStats> {
  return request<AccountStats>('/api/stats');
}

export function listFiles(): Promise<{ files: FileDto[] }> {
  return request<{ files: FileDto[] }>('/api/files');
}

export function deleteFile(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>('/api/files/' + id, { method: 'DELETE' });
}

export function deleteAllFiles(): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>('/api/files', { method: 'DELETE' });
}

export interface UploadOptions {
  format: OutputFormat;
  quality: number;
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
}

/**
 * Upload with XMLHttpRequest rather than fetch: it is still the only way to get
 * real upload progress, which matters for a 40 MB batch of photos.
 */
export function uploadFiles(files: File[], options: UploadOptions): Promise<ConvertResult> {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    form.append('format', options.format);
    form.append('quality', String(options.quality));
    for (const file of files) form.append('files', file, file.name);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', BASE + '/api/convert');
    xhr.withCredentials = true;
    xhr.responseType = 'json';

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) options.onProgress?.(event.loaded / event.total);
    };

    xhr.onload = () => {
      const body = xhr.response as (ConvertResult & { error?: string }) | null;
      if (xhr.status >= 200 && xhr.status < 300 && body?.files) {
        resolve({ files: body.files, stats: body.stats });
      } else {
        reject(new Error(body?.error || 'Upload failed (' + xhr.status + ')'));
      }
    };

    xhr.onerror = () => reject(new Error('Network error while uploading'));
    xhr.onabort = () => reject(new DOMException('Upload cancelled', 'AbortError'));

    options.signal?.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(form);
  });
}

export function convertRemote(
  files: PickedFile[],
  options: { format: OutputFormat; quality: number },
): Promise<ConvertResult> {
  return request<ConvertResult>('/api/convert/remote', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ files, format: options.format, quality: options.quality }),
  });
}

export function zipUrl(ids?: string[]): string {
  const query = ids && ids.length > 0 ? '?ids=' + ids.map(encodeURIComponent).join(',') : '';
  return BASE + '/api/files/zip' + query;
}
